import React from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import {
  DIGO_CLIENT_USER_TYPE,
  DIGO_WORKER_USER_TYPE,
  signupLinkProps,
} from '../LandingPage/digoServices';

import css from './HowItWorksPage.module.css';

const CLIENT_STEPS = [
  {
    num: '1',
    title: 'Publie ta demande',
    desc:
      "Décris ta tâche, le moment souhaité et l'adresse. La plupart des demandes prennent moins de 2 minutes.",
  },
  {
    num: '2',
    title: 'Reçois des offres',
    desc:
      'Des travailleurs vérifiés dans ta zone te répondent avec leur tarif et leurs disponibilités.',
  },
  {
    num: '3',
    title: 'Choisis ton travailleur',
    desc:
      'Consulte les profils, les évaluations et les certifications. Tu choisis la personne en qui tu as confiance.',
  },
  {
    num: '4',
    title: "C'est fait — paye en sécurité",
    desc:
      "Le paiement est retenu jusqu'à ta confirmation. Une fois satisfait, tu libères les fonds. Sans surprise.",
  },
];

const WORKER_STEPS = [
  {
    num: '1',
    title: 'Crée ton profil',
    desc:
      'Inscris-toi gratuitement. Renseigne tes compétences et ta zone, puis vérifie ton identité en ligne en quelques minutes.',
  },
  {
    num: '2',
    title: 'Parcours les mandats',
    desc:
      'Consulte les demandes publiées près de chez toi. Filtre par catégorie, budget ou distance.',
  },
  {
    num: '3',
    title: 'Soumets ton offre',
    desc:
      "Réponds aux mandats qui t'intéressent : propose ton prix, ta disponibilité et un message personnalisé.",
  },
  {
    num: '4',
    title: 'Complète et sois payé',
    desc:
      'Une fois le travail terminé et accepté, le paiement est viré directement. Les évaluations construisent ta réputation.',
  },
];

const ShieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <polyline points="9 12 11 14 15 10" />
  </svg>
);

const LockIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const StarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const LocationIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className={css.mockIcon} aria-hidden="true">
    <path
      d="M8 1.5a4 4 0 014 4c0 3-4 9-4 9s-4-6-4-9a4 4 0 014-4z"
      stroke="currentColor"
      strokeWidth="1.2"
    />
    <circle cx="8" cy="5.5" r="1.5" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className={css.mockIcon} aria-hidden="true">
    <rect x="1.5" y="3" width="13" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
    <path
      d="M1.5 7h13M5 1.5v3M11 1.5v3"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
  </svg>
);

const TRUST_ITEMS = [
  {
    icon: <ShieldIcon />,
    title: 'Travailleurs vérifiés',
    desc: "Vérification d'identité et antécédents judiciaires pour chaque professionnel actif.",
  },
  {
    icon: <LockIcon />,
    title: 'Paiement protégé',
    desc:
      "L'argent est retenu jusqu'à ta confirmation de satisfaction. Aucun risque d'arnaque ou de non-paiement.",
  },
  {
    icon: <StarIcon />,
    title: 'Évaluations transparentes',
    desc:
      'Chaque transaction génère une évaluation mutuelle. Tu sais toujours avec qui tu fais affaire.',
  },
];

const BAR_HEIGHTS = [35, 60, 48, 85, 55, 72, 100];

export const HowItWorksPageComponent = ({ scrollingDisabled }) => {
  return (
    <Page
      title="Comment ça fonctionne — DIGO"
      description="Découvrez comment DIGO fonctionne pour les clients et les travailleurs au Québec. Publiez une tâche ou trouvez des mandats en quelques étapes."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          {/* ─── HERO ─────────────────────────────────────────── */}
          <section className={css.hero}>
            <div className={css.heroInner}>
              <div className={css.heroBadge}>Comment ça fonctionne</div>
              <h1 className={css.heroTitle}>Simple pour tout le monde.</h1>
              <p className={css.heroSubtitle}>
                Que tu cherches de l&apos;aide ou que tu offres tes services, DIGO te guide à chaque
                étape avec clarté et confiance.
              </p>
              <div className={css.heroNav}>
                <a href="#clients" className={css.heroNavLinkPrimary}>
                  Je suis client →
                </a>
                <a href="#travailleurs" className={css.heroNavLinkAlt}>
                  Je suis travailleur →
                </a>
              </div>
            </div>
          </section>

          {/* ─── CLIENTS ──────────────────────────────────────── */}
          <section id="clients" className={css.clientSection}>
            <div className={css.splitInner}>
              <div className={css.splitLeft}>
                <span className={css.badge}>Pour les clients</span>
                <h2 className={css.splitTitle}>Délègue en 4 étapes.</h2>
                <p className={css.splitSubtitle}>
                  Décris ce dont tu as besoin, reçois des offres de travailleurs vérifiés et paye en
                  toute sécurité. C&apos;est ça, DIGO.
                </p>
                <ol className={css.steps}>
                  {CLIENT_STEPS.map(s => (
                    <li key={s.num} className={css.step}>
                      <div className={css.stepNum}>{s.num}</div>
                      <div className={css.stepContent}>
                        <div className={css.stepTitle}>{s.title}</div>
                        <p className={css.stepDesc}>{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <NamedLink {...signupLinkProps(DIGO_CLIENT_USER_TYPE)} className={css.ctaLime}>
                  Créer un compte gratuit →
                </NamedLink>
              </div>

              <div className={css.splitRight}>
                <div className={css.mockCard}>
                  <div className={css.mockCardHeader}>
                    <span className={css.mockCategory}>Montage &amp; installation</span>
                    <span className={css.mockPrice}>60 $</span>
                  </div>
                  <h3 className={css.mockTitle}>Montage bibliothèque IKEA Billy × 3</h3>
                  <div className={css.mockMeta}>
                    <div className={css.mockMetaRow}>
                      <LocationIcon />
                      Plateau-Mont-Royal, Montréal
                    </div>
                    <div className={css.mockMetaRow}>
                      <CalendarIcon />
                      Samedi avant 14 h
                    </div>
                  </div>
                  <div className={css.mockDivider} />
                  <div className={css.mockFooter}>
                    <div className={css.mockOffers}>
                      <div className={css.mockAvatars}>
                        {['ML', 'JF', 'SC'].map(initials => (
                          <div key={initials} className={css.mockAvatar}>
                            {initials}
                          </div>
                        ))}
                      </div>
                      <span className={css.mockOffersText}>3 offres reçues</span>
                    </div>
                    <div className={css.mockRating}>★ 4.8</div>
                  </div>
                </div>

                <div className={css.mockReviewCard}>
                  <div className={css.mockReviewStars}>★★★★★</div>
                  <p className={css.mockReviewText}>
                    &laquo;&nbsp;Rapide, propre et très professionnel. Je recommande !&nbsp;&raquo;
                  </p>
                  <div className={css.mockReviewAuthor}>— Marie L., Rosemont</div>
                </div>
              </div>
            </div>
          </section>

          {/* ─── TRAVAILLEURS ─────────────────────────────────── */}
          <section id="travailleurs" className={css.workerSection}>
            <div className={css.splitInner}>
              <div className={css.splitLeft}>
                <span className={css.badgeDark}>Pour les travailleurs</span>
                <h2 className={css.splitTitleDark}>Travaille à ton rythme.</h2>
                <p className={css.splitSubtitleDark}>
                  Inscris-toi gratuitement, choisis tes mandats et travaille selon tes
                  disponibilités. Tu gardes le contrôle.
                </p>
                <ol className={css.stepsDark}>
                  {WORKER_STEPS.map(s => (
                    <li key={s.num} className={css.stepDark}>
                      <div className={css.stepNumDark}>{s.num}</div>
                      <div className={css.stepContent}>
                        <div className={css.stepTitleDark}>{s.title}</div>
                        <p className={css.stepDescDark}>{s.desc}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <NamedLink {...signupLinkProps(DIGO_WORKER_USER_TYPE)} className={css.ctaLime}>
                  Devenir travailleur DIGO →
                </NamedLink>
              </div>

              <div className={css.splitRight}>
                <div className={css.earningsCard}>
                  <div className={css.earningsHeader}>
                    <span className={css.earningsLabel}>Revenus ce mois</span>
                    <span className={css.earningsAmount}>1 280 $</span>
                  </div>
                  <div className={css.earningsBars}>
                    {BAR_HEIGHTS.map((h, i) => (
                      <div
                        key={i}
                        className={
                          i === BAR_HEIGHTS.length - 1 ? css.earningsBarAccent : css.earningsBar
                        }
                        style={{ height: `${h}%` }}
                      />
                    ))}
                  </div>
                  <div className={css.earningsDivider} />
                  <div className={css.earningsMeta}>
                    <div className={css.earningsMetaItem}>
                      <span className={css.earningsMetaVal}>12</span>
                      <span className={css.earningsMetaLabel}>mandats complétés</span>
                    </div>
                    <div className={css.earningsMetaItem}>
                      <span className={css.earningsMetaVal}>★ 4.9</span>
                      <span className={css.earningsMetaLabel}>note moyenne</span>
                    </div>
                    <div className={css.earningsMetaItem}>
                      <span className={css.earningsMetaVal}>0 $</span>
                      <span className={css.earningsMetaLabel}>frais d&apos;inscription</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ─── CONFIANCE ────────────────────────────────────── */}
          <section className={css.trustSection}>
            <div className={css.trustInner}>
              <div className={css.trustHeader}>
                <h2 className={css.sectionTitle}>Ce qui rend DIGO différent</h2>
                <p className={css.sectionSubtitle}>
                  Chaque transaction est encadrée pour protéger les deux parties.
                </p>
              </div>
              <div className={css.trustGrid}>
                {TRUST_ITEMS.map((item, i) => (
                  <div key={i} className={css.trustCard}>
                    <div className={css.trustIcon}>{item.icon}</div>
                    <h3 className={css.trustTitle}>{item.title}</h3>
                    <p className={css.trustDesc}>{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ─── CTA DOUBLE ───────────────────────────────────── */}
          <section className={css.cta}>
            <div className={css.ctaInner}>
              <div className={css.ctaCard}>
                <div className={css.ctaIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                </div>
                <h3 className={css.ctaTitle}>Tu cherches quelqu&apos;un ?</h3>
                <p className={css.ctaDesc}>
                  Publie tes tâches gratuitement et reçois des offres de travailleurs vérifiés dans
                  ta zone.
                </p>
                <NamedLink
                  {...signupLinkProps(DIGO_CLIENT_USER_TYPE)}
                  className={css.ctaButtonPrimary}
                >
                  Créer un compte gratuit
                </NamedLink>
              </div>

              <div className={css.ctaDivider} aria-hidden="true" />

              <div className={css.ctaCard}>
                <div className={css.ctaIcon}>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="2" y="7" width="20" height="14" rx="2" />
                    <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
                  </svg>
                </div>
                <h3 className={css.ctaTitle}>Tu veux travailler ?</h3>
                <p className={css.ctaDesc}>
                  Rejoins notre réseau de travailleurs certifiés. Travaille à ton rythme, à tes
                  conditions, sans frais d&apos;inscription.
                </p>
                <NamedLink
                  {...signupLinkProps(DIGO_WORKER_USER_TYPE)}
                  className={css.ctaButtonSecondary}
                >
                  Devenir travailleur DIGO
                </NamedLink>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HowItWorksPage = connect(mapStateToProps)(HowItWorksPageComponent);

export default HowItWorksPage;
